import { ChangeEvent, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { InputBar, Pagination } from '~/ui';
import { FullMenuTable } from './FullMenuTable';
import { Select } from 'antd';
import '~/assets/css/select-table.css';
import styles from './FullMenuBoard.module.scss';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { getPaginate } from '~/utils/getPaginate';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';

interface SelectedPage {
  selected: number;
}

interface EditFoodMenuBoardProps {
  foodId: number;
  currentDate: string;
}

interface Points {
  page: number;
}

interface DishFilters {
  parentCode: string;
  measure: string;
  hasPhoto: string;
  isVisible: string;
}

const DEFAULT_FILTERS: DishFilters = {
  parentCode: '',
  measure: '',
  hasPhoto: '',
  isVisible: '',
};

export const FullMenuBoard = ({
  foodId,
  currentDate,
}: EditFoodMenuBoardProps): JSX.Element => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'dishes-points',
      exclude: [],
    });

  const {
    storedInitialValues: storedFilters,
    create: saveFilters,
  } = useLocalStorage<DishFilters>({
    initialValues: DEFAULT_FILTERS,
    key: 'dishes-filters',
    exclude: [],
  });

  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { value, paginate } = getPaginate('foodMenu');
  const [page, setPage] = useState<number>(paginate || 1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [filters, setFilters] = useState<DishFilters>(storedFilters || DEFAULT_FILTERS);
  const { t } = useTranslation();

  const { data: categories = [] } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/ifcm-wares-categories', {
        params: { food_id: foodId },
      });
      return data as { code: string; name: string }[];
    },
    queryKey: ['dish-categories', foodId],
    enabled: !!foodId,
  });
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const [changingId, setChangingId] = useState<number | null>(null);

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const hasActiveFilters =
    filters.parentCode !== '' ||
    filters.measure !== '' ||
    filters.hasPhoto !== '' ||
    filters.isVisible !== '';

  const handleFilterChange = (key: keyof DishFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
    date: currentDate,
    food_id: foodId,
    ...(filters.parentCode && { parent_code: filters.parentCode }),
    ...(filters.measure && { measure: filters.measure }),
    ...(filters.hasPhoto !== '' && { has_photo: filters.hasPhoto === 'true' }),
    ...(filters.isVisible !== '' && { is_visible: filters.isVisible === 'true' }),
  };

  const {
    data: menu,
    isError,
    error,
    refetch,
    isLoading: isMenuLoading,
  } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/ifcm-wares-all', {
        params,
      });
      return response.data;
    },
    queryKey: ['menu-dishes', foodId, params],
    keepPreviousData: true,
  });

  const { mutateAsync: updateVisibility, isLoading: isUpdateVisibilityLoading } = useMutation(
    async ({ id, isVisible }: { id: number; isVisible: boolean }) => {
      setChangingId(id);
      const response = await instance.post('admin/ifcm-wares-visibility', {
        ware_ids: [id],
        visible: isVisible,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('dish_visability_changed_successfully'));
        refetch();
        setChangingId(null);
      },
      onError: () => {
        toastError(t('dish_visability_changed_error'));
        setChangingId(null);
      },
    }
  );

  let content: React.ReactNode;

  if (isError) {
    content = <ErrorMessage error={error} />;
  } else {
    content = (
      <FullMenuTable
        menu={menu?.data ?? []}
        isLoading={isMenuLoading}
        sort={sort}
        setSort={setSort}
        foodId={foodId}
        updateVisibility={(id: number, isVisible: boolean) =>
          updateVisibility({ id, isVisible })
        }
        isUpdateVisibilityLoading={isUpdateVisibilityLoading}
        changingId={changingId}
      />
    );
  }

  useEffect(() => {
    setPage(paginate);
  }, [paginate]);

  return (
    <Board>
      <div className={styles.editFoodMenuBoardOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <InputBar
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          value={searchTerm}
          placeholder={t('search_by_name')}
          classNameInput={styles.editFoodMenuBoardInput}
          label={`${t('search')}:`}
          star={false}
          classNameInputWrapper={styles.editFoodMenuBoardInputWrapper}
        />
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterItem}>
          <span className={styles.filterLabel}>{t('category')}:</span>
          <div className={styles.filterSelectWrapper}>
            <Select
              value={filters.parentCode || undefined}
              onChange={(val: string | undefined) => handleFilterChange('parentCode', val ?? '')}
              placeholder={t('all_user')}
              allowClear
              popupMatchSelectWidth={false}
            >
              {categories.map(cat => (
                <Select.Option key={cat.code} value={cat.code}>{cat.name}</Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <div className={styles.filterItem}>
          <span className={styles.filterLabel}>{t('measure')}:</span>
          <div className={styles.filterSelectWrapper}>
            <Select
              value={filters.measure || undefined}
              onChange={(val: string | undefined) => handleFilterChange('measure', val ?? '')}
              placeholder={t('all_user')}
              allowClear
            >
              <Select.Option value="г">г</Select.Option>
              <Select.Option value="мл">мл</Select.Option>
              <Select.Option value="шт">шт</Select.Option>
            </Select>
          </div>
        </div>

        <div className={styles.filterItem}>
          <span className={styles.filterLabel}>Фото:</span>
          <div className={styles.filterSelectWrapper}>
            <Select
              value={filters.hasPhoto || undefined}
              onChange={(val: string | undefined) => handleFilterChange('hasPhoto', val ?? '')}
              placeholder={t('all_user')}
              allowClear
            >
              <Select.Option value="true">С фото</Select.Option>
              <Select.Option value="false">Без фото</Select.Option>
            </Select>
          </div>
        </div>

        <div className={styles.filterItem}>
          <span className={styles.filterLabel}>{t('status')}:</span>
          <div className={styles.filterSelectWrapper}>
            <Select
              value={filters.isVisible || undefined}
              onChange={(val: string | undefined) => handleFilterChange('isVisible', val ?? '')}
              placeholder={t('all_user')}
              allowClear
            >
              <Select.Option value="true">Отображается</Select.Option>
              <Select.Option value="false">Скрыто</Select.Option>
            </Select>
          </div>
        </div>

        {hasActiveFilters && (
          <button className={styles.clearFiltersBtn} onClick={clearFilters}>
            {t('reset_filters')}
          </button>
        )}
      </div>

      {content}
      <div className={styles.editFoodMenuBoardPagination}>
        <TableAmountPage
          firstRow={menu?.meta?.from}
          lastRow={menu?.meta?.to}
          total={menu?.meta?.total}
        />
        <Pagination
          forcePage={paginate}
          searchParam={value}
          pageCount={menu?.meta?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
